import dayjs from 'dayjs';

import type { ChipIntent } from '@/components/common/chip';
import { formatInAppTz } from '@/lib/date';

import type { OrderStatus } from './types';

/** Order Date & Time: the UTC wire instant on the application (Eastern) clock, so a row reads as the
 *  Eastern day the date filter asked for (ADO 120986; ADR-0007). `-` when null: a pre-placement
 *  Order has no order date (feature 122355). */
export function formatOrderDateTime(iso: string | null): string {
  return iso ? formatInAppTz(iso, 'D MMM YYYY | hh:mm A') : '-';
}

/** The Eastern calendar day of an **instant** (the order detail's Order Date and Payment Date), `-`
 *  when null. Not for a date-only wire value — that is `formatDeliveryDate` (ADO 120986). */
export function formatOrderDate(iso: string | null): string {
  return iso ? formatInAppTz(iso, 'D MMM YYYY') : '-';
}

/** Delivery Date: plain date, `-` when null (story 102117). */
export function formatDeliveryDate(value: string | null): string {
  return value ? dayjs(value).format('D MMM YYYY') : '-';
}

/** A per-device Rating's, or the written review's, submission/updated date — absolute, never
 *  relative (AC 6f). Same `D MMM YYYY` pattern as formatDeliveryDate, kept as its own function
 *  since this call site never has a null to fall back on (callers already branch on isRated /
 *  comment !== null before calling it). */
export function formatReviewDate(iso: string): string {
  return formatOrderDate(iso);
}

/** Status label + Figma colorway (chip intents verified: approved=green, published=blue,
 *  review=yellow, attention=red, neutral=grey, wip=orange), keyed on the wire enum. Order Placed is
 *  yellow per Figma 12:210267 (ADO 121240); Pending Confirmation is grey (`neutral`) and Failed is
 *  orange (`wip`) per Figma 2704:567422 (feature 122355). Key order is lifecycle order and is the
 *  filter-option order on every drawer that derives its options from this map. */
export const ORDER_STATUS_CHIP: Record<OrderStatus, { label: string; intent: ChipIntent }> = {
  payment_processing: { label: 'Pending Confirmation', intent: 'neutral' },
  payment_failed: { label: 'Failed', intent: 'wip' },
  order_placed: { label: 'Order Placed', intent: 'review' },
  shipped: { label: 'Shipped', intent: 'published' },
  delivered: { label: 'Delivered', intent: 'approved' },
  cancelled: { label: 'Cancelled', intent: 'attention' },
};
